import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./tests.scss";

const SLIDE_DURATION_MS = 280;
const RADAR_CENTER = 150;
const RADAR_RADIUS = 102;

const getProgressCacheKey = (testId) => `test-progress:${testId}`;

const readCachedProgress = (testId, questionCount) => {
  if (typeof window === "undefined") return null;

  try {
    const rawProgress = window.localStorage.getItem(getProgressCacheKey(testId));
    if (!rawProgress) return null;

    const progress = JSON.parse(rawProgress);
    const index = Number(progress.currentQuestionIndex);

    if (
      !progress.answers ||
      typeof progress.answers !== "object" ||
      Number.isNaN(index) ||
      index < 0 ||
      index >= questionCount
    ) {
      return null;
    }

    return {
      answers: progress.answers,
      currentQuestionIndex: index,
      updatedAt: progress.updatedAt,
    };
  } catch (error) {
    return null;
  }
};

const writeCachedProgress = (testId, progress) => {
  if (typeof window === "undefined") return;

  window.localStorage.setItem(
    getProgressCacheKey(testId),
    JSON.stringify({
      ...progress,
      updatedAt: new Date().toISOString(),
    })
  );
};

const clearCachedProgress = (testId) => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(getProgressCacheKey(testId));
};

const getRadarPoint = (index, total, percent, radius = RADAR_RADIUS) => {
  const angle = (Math.PI * 2 * index) / total - Math.PI / 2;
  const scaledRadius = radius * (percent / 100);

  return {
    x: RADAR_CENTER + Math.cos(angle) * scaledRadius,
    y: RADAR_CENTER + Math.sin(angle) * scaledRadius,
  };
};

const getRadarPolygonPoints = (items, percent = null) => {
  return items
    .map((item, index) => {
      const point = getRadarPoint(index, items.length, percent ?? item.percent);
      return `${point.x},${point.y}`;
    })
    .join(" ");
};

const fallbackData = {
  backToTests: "Back to tests",
  results: "Results",
  topInterests: "Your top interests",
  match: "match",
  resumeTitle: "Continue where you left off?",
  resumeProgress: "You answered {answered} of {total} questions.",
  continue: "Continue",
  startOver: "Start over",
  questionsSoon: "Questions will be added soon.",
  answerLabels: {},
  riasec: {},
  items: {},
};

const formatText = (template, values) => {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value),
    template
  );
};

const getLocalizedValue = (value, language) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  return value[language] || value.en || value.mn || "";
};

const getLocalizedList = (value, language) => {
  if (!value || Array.isArray(value)) return value || [];
  return value[language] || value.en || value.mn || [];
};

const InterestProfilerTest = ({ test, data = fallbackData, language = "en" }) => {
  const text = { ...fallbackData, ...data };
  const testText = {
    ...test,
    ...(text.items?.[test.id] || {}),
  };
  const questions = useMemo(() => {
    return test.sections.flatMap((section) => section.questions || []);
  }, [test.sections]);

  const cachedProgress = useMemo(() => {
    return readCachedProgress(test.id, questions.length);
  }, [test.id, questions.length]);

  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isSliding, setIsSliding] = useState(false);
  const [results, setResults] = useState(null);
  const [resumeProgress, setResumeProgress] = useState(cachedProgress);

  const currentQuestion = questions[currentQuestionIndex];

  const calculateResults = (finalAnswers) => {
    const initialScores = test.riasec.reduce((scores, interest) => ({
      ...scores,
      [interest.code]: {
        ...interest,
        ...(text.riasec?.[interest.code] || {}),
        name: getLocalizedValue((text.riasec?.[interest.code] || interest).name, language),
        description: getLocalizedValue((text.riasec?.[interest.code] || interest).description, language),
        interests: getLocalizedList((text.riasec?.[interest.code] || interest).interests, language),
        score: 0,
        maxScore: 0,
      },
    }), {});

    questions.forEach((question) => {
      const weights = question.weights || {};

      Object.entries(weights).forEach(([code, weight]) => {
        if (!initialScores[code] || !weight) return;
        initialScores[code].score += finalAnswers[question.id] * weight;
        initialScores[code].maxScore += 5 * weight;
      });
    });

    const chartItems = test.riasec.map((interest) => {
      const score = initialScores[interest.code];

      return {
        ...score,
        percent: score.maxScore > 0
          ? Math.round((score.score / score.maxScore) * 100)
          : 0,
      };
    });

    const ranked = [...chartItems].sort((a, b) => b.percent - a.percent);

    const secondScore = ranked[1]?.percent || 0;
    const topMatches = ranked[0].percent - secondScore <= 10
      ? ranked.slice(0, 2)
      : ranked.slice(0, 1);

    return { chartItems, ranked, topMatches };
  };

  const handleOptionClick = (optionValue) => {
    if (isSliding || !currentQuestion) return;

    const nextAnswers = {
      ...answers,
      [currentQuestion.id]: optionValue,
    };
    const isFinalQuestion = currentQuestionIndex === questions.length - 1;
    const nextQuestionIndex = Math.min(currentQuestionIndex + 1, questions.length - 1);

    setAnswers(nextAnswers);
    setIsSliding(true);

    if (isFinalQuestion) {
      clearCachedProgress(test.id);
    } else {
      writeCachedProgress(test.id, {
        answers: nextAnswers,
        currentQuestionIndex: nextQuestionIndex,
      });
    }

    window.setTimeout(() => {
      if (isFinalQuestion) {
        setResults(calculateResults(nextAnswers));
      } else {
        setCurrentQuestionIndex(nextQuestionIndex);
      }

      setIsSliding(false);
    }, SLIDE_DURATION_MS);
  };

  const handleContinue = () => {
    setAnswers(resumeProgress.answers);
    setCurrentQuestionIndex(resumeProgress.currentQuestionIndex);
    setResumeProgress(null);
  };

  const handleRestart = () => {
    clearCachedProgress(test.id);
    setAnswers({});
    setCurrentQuestionIndex(0);
    setResults(null);
    setResumeProgress(null);
  };

  if (results) {
    return (
      <div className="interest-profiler-page interest-profiler-page--results">
        <main className="interest-profiler-results">
          <Link to="/tests" className="test-runner__back">{text.backToTests}</Link>
          <section className="test-results" aria-live="polite">
            <h3>{getLocalizedValue(testText.resultTitle, language) || text.results}</h3>
            <div className="test-results__radar">
              <svg className="riasec-radar" viewBox="0 0 300 300" role="img" aria-label={getLocalizedValue(testText.resultTitle, language) || text.results}>
                {[25, 50, 75, 100].map((level) => (
                  <polygon
                    key={level}
                    className="riasec-radar__grid"
                    points={getRadarPolygonPoints(results.chartItems, level)}
                  />
                ))}

                {results.chartItems.map((interest, index) => {
                  const edge = getRadarPoint(index, results.chartItems.length, 100);
                  const label = getRadarPoint(index, results.chartItems.length, 118);

                  return (
                    <g key={interest.code}>
                      <line
                        className="riasec-radar__axis"
                        x1={RADAR_CENTER}
                        y1={RADAR_CENTER}
                        x2={edge.x}
                        y2={edge.y}
                      />
                      <text
                        className="riasec-radar__label"
                        x={label.x}
                        y={label.y}
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        {interest.code}
                      </text>
                    </g>
                  );
                })}

                <polygon
                  className="riasec-radar__area"
                  points={getRadarPolygonPoints(results.chartItems)}
                />
                <polyline
                  className="riasec-radar__line"
                  points={getRadarPolygonPoints(results.chartItems)}
                />
                {results.chartItems.map((interest, index) => {
                  const point = getRadarPoint(index, results.chartItems.length, interest.percent);

                  return (
                    <circle
                      key={interest.code}
                      className="riasec-radar__point"
                      cx={point.x}
                      cy={point.y}
                      r="4"
                    />
                  );
                })}
              </svg>

              <div className="riasec-chart-legend">
                {results.chartItems.map((interest) => (
                  <div key={interest.code} className="riasec-chart-legend__item">
                    <span className="riasec-chart-legend__graphic">{interest.graphic}</span>
                    <div>
                      <strong>{interest.code} - {interest.name}</strong>
                      <span>{interest.percent}% {text.match}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="test-results__recommendations">
              <h4>{text.topInterests}</h4>
              <div className="test-results__matches">
                {results.topMatches.map((interest) => (
                  <article key={interest.code} className="interest-match">
                    <div className="interest-match__heading">
                      <span>{interest.graphic}</span>
                      <strong>{interest.name}</strong>
                    </div>
                    <p>{interest.description}</p>
                    <div className="interest-match__tags">
                      {interest.interests.map((item) => (
                        <span key={item}>{item}</span>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </main>
      </div>
    );
  }

  if (resumeProgress) {
    return (
      <div className="interest-profiler-page">
        <main className="interest-profiler-resume">
          <p className="interest-question__eyebrow">{getLocalizedValue(testText.title, language)}</p>
          <h1>{text.resumeTitle}</h1>
          <p>
            {formatText(text.resumeProgress, {
              answered: Object.keys(resumeProgress.answers).length,
              total: questions.length,
            })}
          </p>
          <div className="interest-profiler-resume__actions">
            <button type="button" className="interest-profiler-resume__primary" onClick={handleContinue}>
              {text.continue}
            </button>
            <button type="button" className="interest-profiler-resume__secondary" onClick={handleRestart}>
              {text.startOver}
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="interest-profiler-page">
      <main className="interest-profiler-stage">
        <div className="interest-profiler-progress">
          <span>{currentQuestionIndex + 1}</span>
          <div className="interest-profiler-progress__track" aria-hidden="true">
            <div
              className="interest-profiler-progress__bar"
              style={{ width: `${((currentQuestionIndex + 1) / Math.max(questions.length, 1)) * 100}%` }}
            />
          </div>
          <span>{questions.length}</span>
        </div>

        {currentQuestion ? (
          <section className={`interest-question ${isSliding ? "interest-question--slide-left" : ""}`}>
            <p className="interest-question__eyebrow">{getLocalizedValue(testText.title, language)}</p>
            <h1>{getLocalizedValue(currentQuestion.prompt, language)}</h1>

            <div className="interest-question__options" aria-label="Answer choices">
              {test.options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className="interest-question__option"
                  onClick={() => handleOptionClick(option.value)}
                  disabled={isSliding}
                >
                  <span className="interest-question__emoji">{option.emoji}</span>
                  <span className="interest-question__label">{text.answerLabels?.[option.value] || getLocalizedValue(option.label, language)}</span>
                </button>
              ))}
            </div>
          </section>
        ) : (
          <p className="test-section__empty">{text.questionsSoon}</p>
        )}
      </main>
    </div>
  );
};

export default InterestProfilerTest;
